import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import './feedback.css';

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  danger?: boolean;
}

interface ConfirmRequest {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setRequest({ options, resolve });
      }),
    [],
  );

  const close = useCallback(
    (result: boolean) => {
      request?.resolve(result);
      setRequest(null);
    },
    [request],
  );

  useEffect(() => {
    if (request === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request !== null && (
        <div className="confirm-overlay" onClick={() => close(false)}>
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label={request.options.title}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="confirm-title">{request.options.title}</h2>
            {request.options.body !== undefined && (
              <p className="confirm-body">{request.options.body}</p>
            )}
            <div className="confirm-actions">
              <button type="button" className="ghost" onClick={() => close(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={request.options.danger === true ? 'danger' : 'primary'}
                onClick={() => close(true)}
              >
                {request.options.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): { confirm: ConfirmFn } {
  const ctx = useContext(ConfirmContext);
  if (ctx === null) throw new Error('useConfirm must be used within a ConfirmProvider');
  return { confirm: ctx };
}
